import { useEffect, useState } from 'react';
import { useApi } from 'shared_components/context';
import type { ArtistMember } from './ArtistEditorModal';

/** How long to wait after the last keystroke before calling the API. */
export const ARTIST_LOOKUP_DEBOUNCE_MS = 300;

export interface ArtistLookupResult {
  id: string;
  stageName: string;
  members?: ArtistMember[];
}

export interface UseArtistLookupOptions {
  /** The client whose discography is being searched. */
  userId?: string;
  /**
   * The recording the search is scoped to.
   *
   * Contract 4.4.1 takes this as a URL path segment. `undefined` is not an
   * error: it means create mode (WMC-515), where no recording exists yet, and
   * the search goes to §4.6 endpoint 4 — the recording-less sibling route —
   * instead. Both return the same payload, so nothing downstream branches.
   *
   * Before that route existed the hook simply went quiet here and reported
   * `unavailable`. It no longer needs to.
   */
  parentRecordingId?: string;
  /** False while the form is read-only, which suppresses the lookup entirely. */
  enabled: boolean;
}

export interface UseArtistLookupState {
  query: string;
  setQuery: (q: string) => void;
  results: ArtistLookupResult[];
  loading: boolean;
  /** True when the lookup cannot run at all, as opposed to running and finding
   *  nothing. The two look identical in `results` and must not read the same
   *  in the UI. A missing recording no longer counts — only a missing user or
   *  a read-only form does. */
  unavailable: boolean;
}

/**
 * Debounced artist search (contract 4.4.1).
 *
 * Extracted from `RecordingEditForm` as part of the WMC-515 Phase 0 split so
 * the create flow can reuse the search rather than grow a second copy of it.
 * Behaviour is unchanged from the inline effect: empty queries never reach the
 * API, a failed lookup empties the results rather than surfacing an error, and
 * every keystroke cancels the pending call.
 */
export const useArtistLookup = ({
  userId,
  parentRecordingId,
  enabled,
}: UseArtistLookupOptions): UseArtistLookupState => {
  const { adminApi: api } = useApi();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArtistLookupResult[]>([]);
  const [loading, setLoading] = useState(false);

  const unavailable = !enabled || !userId;

  useEffect(() => {
    if (!enabled || !userId) return;
    const q = query.trim();
    // Only call the API when the user has actually typed something — no
    // empty / wildcard searches.
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        // Two routes, one payload. The recording-bearing one scopes the search
        // to the recording's client; the sibling scopes it to the user alone,
        // which is all a create has.
        const result = parentRecordingId
          ? await api.lookupArtistsLookupArtistsResponse({
              userId,
              parentRecordingId,
              searchTerm: q,
            })
          : await api.lookupArtistsWithoutRecording({ userId, searchTerm: q });
        setResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
            members: (a.members ?? []).map((m) => ({
              id: m.id ?? '',
              stageName: m.stageName ?? '',
            })),
          }))
        );
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, ARTIST_LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, enabled, userId, parentRecordingId, api]);

  return { query, setQuery, results, loading, unavailable };
};
